import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { pathEq } from 'ramda'
import React from 'react'

import CreateButton from '@/app/component/atom/create-button'
import FilterTags from '@/app/component/atom/filters/tags'
import { FilterOption } from '@/app/component/atom/filters/types'
import SearchBar from '@/app/component/atom/search'
import Tooltip from '@/app/component/atom/tooltip'
import IconText from '@/app/component/layout/icon-text'
import TableToolbar from '@/app/component/layout/table/toolbar'
import { SEND_LIMIT } from '@/app/module/logs/definitions'
import { ContactSelection, Filters } from '@/app/types/contact'
import { Column } from '@/config/types'

import ConfigureTable from './configure-table'

type Filter = {
  name: string
  value: string
}

type Search = {
  term: string
  onSubmit: (searchTerm: string) => void
}

type Props = {
  activeFilter: Filter
  filters: Filters
  groupsFilter?: boolean
  headers: Column[]
  loading: boolean
  search?: Search
  selection: ContactSelection

  onAddContacts: () => void
  onAddContactsToGroup: () => void
  onAddGroups: () => void
  onAddSegmentsFields: () => void
  onDeleteContacts: () => void
  onDownloadContacts: () => void
  onFilterChange: (data: { name: string; value: string }) => void
  onManageCustomFields: () => void
  onRemoveContactsFromGroup: () => void
  onSendMessage: () => void
  onUploadContacts: () => void
  reloadContacts: () => void
  setHeaders: (headers: Column[]) => void
  toggleGroups?: () => void
}

const ContactsTableToolbar: React.FC<Props> = ({
  activeFilter,
  filters,
  groupsFilter = false,
  headers,
  loading,
  search,
  selection,
  onAddContacts,
  onAddContactsToGroup,
  onAddGroups,
  onAddSegmentsFields,
  onDeleteContacts,
  onDownloadContacts,
  onFilterChange,
  onManageCustomFields,
  onRemoveContactsFromGroup,
  onSendMessage,
  onUploadContacts,
  reloadContacts,
  setHeaders,
  toggleGroups = () => {},
}) => {
  const theme = useTheme()
  const [bulkActionsMenuAnchor, setBulkActionsMenuAnchor] = React.useState<HTMLElement | null>(null)
  const [createContactMenuAnchor, setCreateContactMenuAnchor] = React.useState<HTMLElement | null>(null)

  const toggleCreateContactMenu = (target: HTMLElement | null = null) => {
    setCreateContactMenuAnchor(target)
  }

  return (
    <TableToolbar>
      <div style={{ display: 'flex', flex: '1 auto' }}>
        <div
          style={{
            display: 'flex',
            margin: '5px',
            marginLeft: '0px',
          }}
        >
          <div id="groups-toggle" onClick={toggleGroups}>
            <Tooltip title="Show/ Hide Group & Segments">
              <div>
                <Button
                  style={{
                    marginRight: '5px',
                  }}
                  variant="outlined"
                  onClick={toggleGroups}
                  startIcon={
                    <Icon
                      style={{
                        transition: 'transform 150ms ease',
                        ...(!groupsFilter && {
                          transform: 'rotate(-180deg)',
                        }),
                      }}
                    >
                      menu_open
                    </Icon>
                  }
                >
                  &nbsp;
                </Button>
              </div>
            </Tooltip>
          </div>

          <div
            id="bulk-actions-button"
            onClick={(e) => {
              if (selection.count > 0) {
                setBulkActionsMenuAnchor(e.currentTarget)
              }
            }}
          >
            <Tooltip
              title={
                selection.count > 0 ? (
                  'Bulk actions'
                ) : (
                  <span style={{ textAlign: 'center', display: 'block', lineHeight: '1.7' }}>
                    Bulk Actions
                    <br />
                    Select contacts to use this option
                  </span>
                )
              }
            >
              <div>
                <Button
                  variant="outlined"
                  onClick={(e) => setBulkActionsMenuAnchor(e.currentTarget)}
                  endIcon={<Icon>arrow_drop_down</Icon>}
                  startIcon={<Icon>more_vert</Icon>}
                >
                  Bulk Actions
                </Button>
              </div>
            </Tooltip>
          </div>
          <Menu
            disableAutoFocusItem={true}
            id="contacts-bulk-menu"
            open={!!bulkActionsMenuAnchor}
            onClose={() => setBulkActionsMenuAnchor(null)}
            anchorEl={bulkActionsMenuAnchor}
          >
            <li
              onClick={() => {
                if (selection.count > 0 && selection.count < SEND_LIMIT) {
                  setBulkActionsMenuAnchor(null)
                  onSendMessage()
                }
              }}
            >
              <Tooltip
                open={bulkActionsMenuAnchor && selection.count >= SEND_LIMIT ? undefined : false}
                background={theme.palette.error.main}
                title={`This feature supports up to ${SEND_LIMIT} contacts at a time only.`}
              >
                <div>
                  <MenuItem component="span" disabled={selection.count < 1}>
                    <IconText>
                      <Icon>sms</Icon>
                      <Typography>Send message</Typography>
                    </IconText>
                  </MenuItem>
                </div>
              </Tooltip>
            </li>
            <li
              onClick={() => {
                if (selection.count > 0 || selection.isAll) {
                  setBulkActionsMenuAnchor(null)
                  onDeleteContacts()
                }
              }}
            >
              <Tooltip title={selection.count < 1 && 'Please select at least one contact.'}>
                <div>
                  <MenuItem component="span" disabled={selection.count < 1}>
                    <IconText>
                      <Icon>delete</Icon>
                      <Typography>Delete contacts</Typography>
                    </IconText>
                  </MenuItem>
                </div>
              </Tooltip>
            </li>
            <li
              onClick={() => {
                if (selection.count > 0) {
                  setBulkActionsMenuAnchor(null)
                  onDownloadContacts()
                }
              }}
            >
              <MenuItem component="span" disabled={selection.count < 1}>
                <IconText>
                  <Icon>cloud_download</Icon>
                  <Typography>Download contacts</Typography>
                </IconText>
              </MenuItem>
            </li>
            <li
              onClick={() => {
                if (selection.count > 0) {
                  setBulkActionsMenuAnchor(null)
                  onAddContactsToGroup()
                }
              }}
            >
              <MenuItem component="span" disabled={selection.count < 1}>
                <IconText>
                  <Icon>group_add</Icon>
                  <Typography>Add contacts to group</Typography>
                </IconText>
              </MenuItem>
            </li>
            <li
              onClick={() => {
                if (
                  !(
                    selection.count < 1 ||
                    !pathEq(['name'], 'groups', activeFilter) ||
                    selection.isAll ||
                    selection.search !== ''
                  )
                ) {
                  if (selection.count > 0) {
                    setBulkActionsMenuAnchor(null)
                    onRemoveContactsFromGroup()
                  }
                }
              }}
            >
              <MenuItem
                component="span"
                disabled={
                  selection.count < 1 ||
                  !pathEq(['name'], 'groups', activeFilter) ||
                  selection.isAll ||
                  selection.search !== ''
                }
              >
                <IconText>
                  <Icon>remove_circle</Icon>
                  <Typography>Remove contacts from group</Typography>
                </IconText>
              </MenuItem>
            </li>
          </Menu>
        </div>
        <FilterTags
          id="contacts-filter-tags"
          filters={filters.data as unknown as Record<string, FilterOption>}
          onChange={onFilterChange}
        />
      </div>

      {search && (
        <SearchBar
          namespace="contacts"
          tooltipTitle="Search contacts"
          disabled={loading}
          searchTerm={search.term}
          onSubmit={(searchTerm) => {
            selection.clearSelection()
            search.onSubmit(searchTerm)
          }}
        />
      )}
      <ConfigureTable headers={headers} setHeaders={setHeaders} />
      <Tooltip title="Create contacts and groups">
        <span>
          <CreateButton
            id="new-contact-menu-button"
            endIcon={<Icon>arrow_drop_down</Icon>}
            onClick={(e) => toggleCreateContactMenu(e.currentTarget)}
          >
            New
          </CreateButton>
        </span>
      </Tooltip>
      <Tooltip title="Reload contacts">
        <IconButton id="reload-contacts" onClick={() => reloadContacts()}>
          <Icon>refresh</Icon>
        </IconButton>
      </Tooltip>
      <Menu
        id="contact-create-menu"
        open={!!createContactMenuAnchor}
        onClose={() => toggleCreateContactMenu()}
        anchorEl={createContactMenuAnchor}
      >
        <MenuItem id="upload-contacts" onClick={() => onUploadContacts()}>
          <IconText>
            <Icon>cloud_upload</Icon>
            <Typography>Upload contacts</Typography>
          </IconText>
        </MenuItem>
        <Divider />
        <MenuItem
          id="add-contact-button"
          onClick={() => {
            toggleCreateContactMenu()
            onAddContacts()
          }}
        >
          <IconText>
            <Icon>person_outline</Icon>
            <Typography>Add contact</Typography>
          </IconText>
        </MenuItem>
        <MenuItem
          id="add-group-button"
          onClick={() => {
            toggleCreateContactMenu()
            onAddGroups()
          }}
        >
          <IconText>
            <Icon>group</Icon>
            <Typography>Add group</Typography>
          </IconText>
        </MenuItem>
        <MenuItem
          id="add-segments-button"
          onClick={() => {
            toggleCreateContactMenu()
            onAddSegmentsFields()
          }}
        >
          <IconText>
            <Icon>group_work</Icon>
            <Typography>Add segment</Typography>
          </IconText>
        </MenuItem>
        <Divider />
        <MenuItem
          id="add-customfields-button"
          onClick={() => {
            onManageCustomFields()
          }}
          sx={{
            '& .MuiTypography-root': {
              color: 'primary.main',
            },
            '&:hover .MuiTypography-root': {
              textDecoration: 'underline',
            },
          }}
        >
          <IconText>
            <Icon sx={{ color: 'primary.main' }}>bookmark</Icon>
            <Typography>Manage custom fields</Typography>
          </IconText>
        </MenuItem>
      </Menu>
    </TableToolbar>
  )
}

export default ContactsTableToolbar
